import React, { useMemo, useState } from 'react';
import * as XLSX from 'xlsx';
import styles from './DocumentPanel.module.css';

// Rows shown per sheet. The download carries everything; the preview stays fast.
export const MAX_PREVIEW_ROWS = 500;
export const MAX_PREVIEW_COLS = 50;

// The cell's text as Excel would show it. A formula written by a library has no
// real saved result until Excel recalculates it: openpyxl leaves it empty
// (SheetJS reads a stub, 'z'), and XlsxWriter stores a placeholder 0. Show the
// formula itself rather than a blank or a 0 that reads like a real total.
const cellText = (cell, staleResults) => {
  if (!cell) return { text: '' };
  if (cell.f && (staleResults || cell.t === 'z' || cell.v === undefined || cell.v === null)) {
    return { text: `=${cell.f}`, formula: true };
  }
  if (cell.t === 'z') return { text: '' };
  if (cell.w !== undefined && cell.w !== null) return { text: String(cell.w) };
  if (cell.v !== undefined && cell.v !== null) return { text: String(cell.v) };
  return { text: '' };
};

// A zip ends with an end-of-central-directory record (PK 05 06) in its last
// 64 KB + 22 bytes. SheetJS can hang the tab on a truncated zip instead of
// throwing, so a file without one is refused before parsing.
const EOCD = [0x50, 0x4b, 0x05, 0x06];
const hasZipEnd = (buffer) => {
  const bytes = new Uint8Array(buffer);
  const stop = Math.max(0, bytes.length - 65557);
  for (let i = bytes.length - EOCD.length; i >= stop; i -= 1) {
    if (bytes[i] === EOCD[0] && bytes[i + 1] === EOCD[1] && bytes[i + 2] === EOCD[2] && bytes[i + 3] === EOCD[3]) {
      return true;
    }
  }
  return false;
};

const readSheets = (buffer) => {
  if (!hasZipEnd(buffer)) throw new Error('incomplete workbook');
  const workbook = XLSX.read(buffer, { type: 'array', cellFormula: true, cellDates: false, sheetStubs: true });
  // fullCalcOnLoad tells Excel to recalculate everything on open: the stored
  // formula results are placeholders, not values.
  const fullCalc = String(workbook.Workbook?.CalcPr?.fullCalcOnLoad ?? '').toLowerCase();
  const staleResults = fullCalc === '1' || fullCalc === 'true';
  return workbook.SheetNames.map((name) => {
    const sheet = workbook.Sheets[name];
    if (!sheet || !sheet['!ref']) return { name, rows: [], totalRows: 0, hasUnsavedFormulas: false };
    const range = XLSX.utils.decode_range(sheet['!ref']);
    const totalRows = range.e.r - range.s.r + 1;
    const lastRow = Math.min(range.e.r, range.s.r + MAX_PREVIEW_ROWS - 1);
    const lastCol = Math.min(range.e.c, range.s.c + MAX_PREVIEW_COLS - 1);
    let hasUnsavedFormulas = false;
    const rows = [];
    for (let r = range.s.r; r <= lastRow; r += 1) {
      const row = [];
      for (let c = range.s.c; c <= lastCol; c += 1) {
        const cell = cellText(sheet[XLSX.utils.encode_cell({ r, c })], staleResults);
        if (cell.formula) hasUnsavedFormulas = true;
        row.push(cell.text);
      }
      rows.push(row);
    }
    return { name, rows, totalRows, hasUnsavedFormulas, truncatedCols: range.e.c > lastCol };
  });
};

/**
 * Read-only preview of an Excel workbook: one tab per sheet, cells rendered as
 * text by React (never as HTML), capped for speed.
 */
export default function XlsxPreview({ buffer }) {
  const sheets = useMemo(() => {
    try {
      return { list: readSheets(buffer) };
    } catch (err) {
      return { error: err };
    }
  }, [buffer]);
  const [active, setActive] = useState(0);

  if (sheets.error) {
    return <p className={styles.errorText} role="alert">This workbook could not be previewed. Download it to open it in Excel.</p>;
  }
  if (!sheets.list.length) {
    return <p className={styles.emptyText}>This workbook has no sheets.</p>;
  }
  const sheet = sheets.list[Math.min(active, sheets.list.length - 1)];

  return (
    <div className={styles.sheetPreview}>
      {sheets.list.length > 1 && (
        <div className={styles.sheetTabs} role="tablist" aria-label="Sheets">
          {sheets.list.map((s, idx) => (
            <button
              key={s.name}
              type="button"
              role="tab"
              aria-selected={idx === active}
              className={`${styles.sheetTab} ${idx === active ? styles.sheetTabActive : ''}`}
              onClick={() => setActive(idx)}
            >
              {s.name}
            </button>
          ))}
        </div>
      )}
      {sheet.hasUnsavedFormulas && (
        <p className={styles.notice}>
          Totals show as formulas here. Excel calculates them when you open the file.
        </p>
      )}
      {sheet.rows.length === 0 ? (
        <p className={styles.emptyText}>This sheet is empty.</p>
      ) : (
        <div className={styles.sheetScroll}>
          <table className={styles.sheetTable} aria-label={`Sheet ${sheet.name}`}>
            <tbody>
              {sheet.rows.map((row, r) => (
                // eslint-disable-next-line react/no-array-index-key
                <tr key={r} className={r === 0 ? styles.sheetHeaderRow : undefined}>
                  {row.map((value, c) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <td key={c} className={styles.sheetCell}>{value}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {(sheet.totalRows > sheet.rows.length || sheet.truncatedCols) && (
        <p className={styles.mutedText}>
          Showing the first {sheet.rows.length} of {sheet.totalRows} rows
          {sheet.truncatedCols ? ` and the first ${MAX_PREVIEW_COLS} columns` : ''}. Download the file to see all of it.
        </p>
      )}
    </div>
  );
}
